import React, { useState } from 'react';
import {
  Box, Typography, Paper, Table, TableCell, TableContainer,
  TableHead, TableRow, Chip, Avatar, Stack, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  MenuItem, Select, FormControl, InputLabel, Tooltip, IconButton, TextField,
  Divider, Drawer, Grid, LinearProgress,
} from '@mui/material';
import TableBody from '../components/PagedTableBody'; // pages rows beyond 10
import EditIcon from '@mui/icons-material/Edit';
import BadgeIcon from '@mui/icons-material/Badge';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import PersonIcon from '@mui/icons-material/Person';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import LockResetIcon from '@mui/icons-material/LockReset';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import ForumIcon from '@mui/icons-material/Forum';
import DeleteUserDialog from '../components/DeleteUserDialog';
import SendMessageDialog from '../components/SendMessageDialog';
import { useQuery, useMutation } from '@apollo/client';
import {
  USERS_QUERY, ROLES_QUERY,
  ASSIGN_ROLE_MUTATION,
  SET_MENU_VISIBILITY_MUTATION,
  DEACTIVATE_USER_MUTATION,
  REACTIVATE_USER_MUTATION,
  ADMIN_CREATE_USER_MUTATION,
  ADMIN_RESET_PASSWORD_MUTATION, SET_ACCOUNT_MANAGER_MUTATION, SET_CONTACT_PHONE_MUTATION,
} from '../graphql/queries';

const FAMILY_ROLE_NAMES = ['bride_family', 'groom_family'];

const ROLE_COLORS = {
  system_admin: 'error',
  matchmaker:   'secondary',
  bride_family: 'primary',
  groom_family: 'info',
  vendor:       'warning',
};

const MENU_VISIBILITY_OPTIONS = [
  { value: 'default',  label: 'Default (All Menus)',
    description: 'User can see all available menus based on their role & permissions' },
  { value: 'selected', label: 'Selected (Core + Contract Services)',
    description: 'User sees only My Profile, Matchmaking, Messages, Meetings, Contracts, Wish List - plus any menus unlocked by their contracted services' },
];

function ProfileField({ label, value }) {
  return (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value || '\u2014'}</Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Grid>
  );
}

export default function AdminUsersPage() {
  const { data, loading, error, refetch } = useQuery(USERS_QUERY);
  const { data: rolesData }  = useQuery(ROLES_QUERY);

  const [assignRole]       = useMutation(ASSIGN_ROLE_MUTATION);
  const [setMenuVis]       = useMutation(SET_MENU_VISIBILITY_MUTATION);
  const [deactivateUser]   = useMutation(DEACTIVATE_USER_MUTATION);
  const [reactivateUser]   = useMutation(REACTIVATE_USER_MUTATION);
  const [createUser]       = useMutation(ADMIN_CREATE_USER_MUTATION);
  // Subscription (and so permissions) a role brings: its default plan, else Free
  const roleFor = (name) => (rolesData?.roles || []).find(r => r.name === name);
  const roleDefaultPlan = (name) => roleFor(name)?.defaultSubscription?.displayName || 'Free';
  const roleLabel = (name) => roleFor(name)?.displayName || name;
  const [resetPassword]    = useMutation(ADMIN_RESET_PASSWORD_MUTATION);
  const [setAccountManager] = useMutation(SET_ACCOUNT_MANAGER_MUTATION);
  const [setContactPhone]   = useMutation(SET_CONTACT_PHONE_MUTATION);

  const [editDialog, setEditDialog]       = useState({ open: false, userId: null, roleName: '', menuVisibility: 'selected' });
  const [createDialog, setCreateDialog]   = useState(false);
  const [profileDrawer, setProfileDrawer] = useState({ open: false, user: null });
  const [deactivateConfirm, setDeactivateConfirm] = useState(null); // user object
  const [deleting, setDeleting]           = useState(null); // user object
  const [messaging, setMessaging]         = useState(null); // user id
  const [notice, setNotice]               = useState('');
  const [resetPwDialog, setResetPwDialog] = useState({ open: false, user: null, password: '', showPassword: false });
  const [newUser, setNewUser]             = useState({ email: '', password: '', roleName: 'bride_family' });
  const [saving, setSaving]               = useState(false);
  const [saveError, setSaveError]         = useState('');
  const [searchText, setSearchText]       = useState('');
  const [filterRole, setFilterRole]       = useState('');
  const [filterStatus, setFilterStatus]   = useState('');

  const openEdit = (u) => {
    setSaveError('');
    // Families: their account manager (a matchmaker) until the function; matchmakers: their number
    const am = u.accountManager?.active ? u.accountManager : null;
    setEditDialog({
      open: true, userId: u.id, roleName: u.role?.name || '', menuVisibility: u.menuVisibility || 'selected',
      isFamily: FAMILY_ROLE_NAMES.includes(u.role?.name), isMatchmaker: u.role?.name === 'matchmaker',
      managerId: am?.userId || '', endsOn: am?.endsOn || '', phone: u.phone || '',
      orig: { managerId: am?.userId || '', endsOn: am?.endsOn || '', phone: u.phone || '' },
    });
  };

  const handleSaveEdit = async () => {
    setSaving(true); setSaveError('');
    try {
      if (editDialog.roleName) {
        await assignRole({ variables: { input: { userId: editDialog.userId, roleName: editDialog.roleName } } });
      }
      await setMenuVis({ variables: { userId: editDialog.userId, menuVisibility: editDialog.menuVisibility } });
      const orig = editDialog.orig || {};
      if (editDialog.isFamily && (editDialog.managerId !== orig.managerId || editDialog.endsOn !== orig.endsOn)) {
        await setAccountManager({ variables: { familyUserId: editDialog.userId, managerUserId: editDialog.managerId || null, endsOn: editDialog.endsOn || null } });
      }
      if (editDialog.isMatchmaker && editDialog.phone !== orig.phone) {
        await setContactPhone({ variables: { userId: editDialog.userId, phone: editDialog.phone } });
      }
      await refetch();
      setEditDialog({ open: false, userId: null, roleName: '', menuVisibility: 'selected' });
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  const handleDeactivate = async () => {
    if (!deactivateConfirm) return;
    setSaving(true);
    try {
      await deactivateUser({ variables: { userId: deactivateConfirm.id } });
      await refetch();
      setDeactivateConfirm(null);
      if (profileDrawer.open && profileDrawer.user?.id === deactivateConfirm.id) {
        setProfileDrawer(d => ({ ...d, user: { ...d.user, isActive: false } }));
      }
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  const handleReactivate = async (u) => {
    setSaving(true);
    try {
      await reactivateUser({ variables: { userId: u.id } });
      await refetch();
      if (profileDrawer.open && profileDrawer.user?.id === u.id) {
        setProfileDrawer(d => ({ ...d, user: { ...d.user, isActive: true } }));
      }
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  const handleCreateUser = async () => {
    if (!newUser.email || !newUser.password) { setSaveError('Email and password are required.'); return; }
    setSaving(true); setSaveError('');
    try {
      await createUser({ variables: { input: { email: newUser.email, password: newUser.password, roleName: newUser.roleName } } });
      await refetch();
      setCreateDialog(false);
      setNewUser({ email: '', password: '', roleName: 'bride_family' });
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  const openResetPassword = (u) => {
    setSaveError('');
    setResetPwDialog({ open: true, user: u, password: '', showPassword: false });
  };

  const handleResetPassword = async () => {
    if (!resetPwDialog.password || resetPwDialog.password.length < 8) {
      setSaveError('Password must be at least 8 characters.');
      return;
    }
    setSaving(true); setSaveError('');
    try {
      await resetPassword({ variables: { userId: resetPwDialog.user.id, newPassword: resetPwDialog.password } });
      setResetPwDialog({ open: false, user: null, password: '', showPassword: false });
    } catch (e) { setSaveError(e.message); }
    finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (error)   return <Alert severity="error" sx={{ m: 3 }}>{error.message}</Alert>;

  const allUsers = data.users || [];
  const filteredUsers = allUsers.filter(u => {
    const matchesSearch = !searchText || u.email.toLowerCase().includes(searchText.toLowerCase());
    const matchesRole   = !filterRole   || u.role?.name === filterRole;
    const matchesStatus = !filterStatus || (filterStatus === 'active' ? u.isActive !== false : u.isActive === false);
    return matchesSearch && matchesRole && matchesStatus;
  });
  const roles = rolesData?.roles || [];

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
        <Stack direction="row" alignItems="center" spacing={2}>
          <BadgeIcon color="primary" sx={{ fontSize: 32 }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>User Management</Typography>
            <Typography variant="body2" color="text.secondary">
              Assign roles, control menu visibility &amp; manage user accounts
            </Typography>
          </Box>
        </Stack>
        <Button variant="contained" startIcon={<PersonAddIcon />} onClick={() => { setSaveError(''); setCreateDialog(true); }}>
          Create User
        </Button>
      </Stack>

      {saveError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>{saveError}</Alert>}
      {notice && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice('')}>{notice}</Alert>}

      {/* Search & filter bar */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} flexWrap="wrap">
        <TextField
          placeholder="Search by email..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          size="small"
          sx={{ flex: 1, minWidth: 200 }}
          InputProps={{
            startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: 'text.disabled' }} />,
            endAdornment: searchText ? (
              <IconButton size="small" onClick={() => setSearchText('')}><ClearIcon fontSize="small" /></IconButton>
            ) : null,
          }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Filter by Role</InputLabel>
          <Select value={filterRole} label="Filter by Role" onChange={e => setFilterRole(e.target.value)}>
            <MenuItem value="">All Roles</MenuItem>
            {roles.map(r => <MenuItem key={r.id} value={r.name}>{r.displayName}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status</InputLabel>
          <Select value={filterStatus} label="Status" onChange={e => setFilterStatus(e.target.value)}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="inactive">Deactivated</MenuItem>
          </Select>
        </FormControl>
        {(searchText || filterRole || filterStatus) && (
          <Button size="small" variant="outlined" onClick={() => { setSearchText(''); setFilterRole(''); setFilterStatus(''); }}>
            Clear Filters
          </Button>
        )}
        <Typography variant="body2" color="text.secondary" alignSelf="center" sx={{ whiteSpace: 'nowrap' }}>
          {filteredUsers.length} of {allUsers.length} users
        </Typography>
      </Stack>

      <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.dark' }}>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>User</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Role</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Menu Visibility</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }}>Permissions</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 700 }} align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No users match the current search / filter.
                </TableCell>
              </TableRow>
            )}
            {filteredUsers.map(u => (
              <TableRow key={u.id} hover sx={{ opacity: u.isActive === false ? 0.6 : 1 }}>
                <TableCell>
                  <Stack direction="row" spacing={1.5} alignItems="center">
                    <Avatar sx={{ width: 34, height: 34, bgcolor: u.isActive === false ? 'grey.400' : 'primary.main', fontSize: '0.85rem' }}>
                      {u.email[0].toUpperCase()}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{u.email}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {u.isVerified ? 'Verified' : 'Unverified'}
                      </Typography>
                      {u.accountManager?.active && (
                        <Typography variant="caption" color="primary.main" display="block">
                          Account manager: {u.accountManager.name}{u.accountManager.endsOn ? ` · until ${u.accountManager.endsOn}` : ''}
                        </Typography>
                      )}
                      {u.familyContacts?.secondary && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Secondary contact: {u.familyContacts.secondary.name}
                          {u.familyContacts.secondary.relation ? ` (${u.familyContacts.secondary.relation})` : ''} · {u.familyContacts.secondary.email}
                        </Typography>
                      )}
                      {u.role?.name === 'matchmaker' && u.phone && (
                        <Typography variant="caption" color="text.secondary" display="block">Phone: {u.phone}</Typography>
                      )}
                    </Box>
                  </Stack>
                </TableCell>

                <TableCell>
                  {u.isActive === false
                    ? <Chip label="Deactivated" color="error" size="small" icon={<BlockIcon sx={{ fontSize: '13px !important' }} />} />
                    : <Chip label="Active" color="success" size="small" icon={<CheckCircleIcon sx={{ fontSize: '13px !important' }} />} />
                  }
                </TableCell>

                <TableCell>
                  {u.role
                    ? <Chip label={u.role.displayName} color={ROLE_COLORS[u.role.name] || 'default'} size="small" />
                    : <Chip label="No Role" size="small" variant="outlined" />}
                </TableCell>

                <TableCell>
                  {(u.menuVisibility || 'selected') === 'selected' ? (
                    <Chip icon={<VisibilityOffIcon sx={{ fontSize: '14px !important' }} />} label="Selected" color="warning" size="small" variant="outlined" />
                  ) : (
                    <Chip icon={<VisibilityIcon sx={{ fontSize: '14px !important' }} />} label="Default" color="success" size="small" variant="outlined" />
                  )}
                </TableCell>

                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {(u.permissions || []).slice(0, 3).map(p => (
                      <Chip key={p} label={p.replace(/_/g, ' ')} size="small" variant="outlined" sx={{ fontSize: '0.65rem' }} />
                    ))}
                    {u.permissions?.length > 3 && (
                      <Chip label={`+${u.permissions.length - 3} more`} size="small" sx={{ fontSize: '0.65rem' }} />
                    )}
                  </Stack>
                </TableCell>

                <TableCell align="center">
                  <Stack direction="row" spacing={0.5} justifyContent="center">
                    <Tooltip title="View Profile">
                      <IconButton size="small" color="info" onClick={() => setProfileDrawer({ open: true, user: u })}>
                        <PersonIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Role & Menu Visibility">
                      <IconButton size="small" color="primary" onClick={() => openEdit(u)} disabled={u.isActive === false}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Reset Password">
                      <IconButton size="small" color="secondary" onClick={() => openResetPassword(u)}>
                        <LockResetIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Send WhatsApp / SMS / email">
                      <IconButton size="small" color="primary" onClick={() => setMessaging(u.id)}>
                        <ForumIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {u.isActive === false ? (
                      <Tooltip title="Reactivate User">
                        <IconButton size="small" color="success" onClick={() => handleReactivate(u)} disabled={saving}>
                          <CheckCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Deactivate User (blocks login)">
                        <IconButton size="small" color="error" onClick={() => { setSaveError(''); setDeactivateConfirm(u); }} disabled={saving}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Delete user (closes their contracts first)">
                      <IconButton size="small" color="error" onClick={() => { setSaveError(''); setDeleting(u); }} disabled={saving}>
                        <DeleteForeverIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* -- View Profile Drawer -- */}
      <Drawer
        anchor="right"
        open={profileDrawer.open}
        onClose={() => setProfileDrawer({ open: false, user: null })}
        PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 3 } }}
      >
        {profileDrawer.user && (() => {
          const u = profileDrawer.user;
          const p = u.profile;
          return (
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={700}>User Profile</Typography>
                <IconButton onClick={() => setProfileDrawer({ open: false, user: null })}><CloseIcon /></IconButton>
              </Stack>

              {/* Account summary */}
              <Paper elevation={0} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 2, mb: 3 }}>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Avatar sx={{ width: 52, height: 52, bgcolor: u.isActive === false ? 'grey.400' : 'primary.main', fontSize: '1.2rem' }}>
                    {u.email[0].toUpperCase()}
                  </Avatar>
                  <Box flex={1}>
                    <Typography variant="body1" fontWeight={700}>{u.email}</Typography>
                    <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap" gap={0.5}>
                      {u.role && <Chip label={u.role.displayName} color={ROLE_COLORS[u.role.name] || 'default'} size="small" />}
                      {u.isActive === false
                        ? <Chip label="Deactivated" color="error" size="small" />
                        : <Chip label="Active" color="success" size="small" />}
                      <Chip label={u.isVerified ? 'Verified' : 'Unverified'} size="small" variant="outlined" />
                    </Stack>
                  </Box>
                </Stack>
                {p && (
                  <Box mt={1.5}>
                    <Stack direction="row" justifyContent="space-between" mb={0.5}>
                      <Typography variant="caption" color="text.secondary">Profile Completion</Typography>
                      <Typography variant="caption" fontWeight={700}>{p.profileCompletion || 0}%</Typography>
                    </Stack>
                    <LinearProgress variant="determinate" value={p.profileCompletion || 0}
                      color={p.profileCompletion >= 80 ? 'success' : p.profileCompletion >= 50 ? 'warning' : 'error'}
                      sx={{ height: 6, borderRadius: 3 }} />
                  </Box>
                )}
              </Paper>

              {p ? (
                <>
                  <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Personal Details</Typography>
                  <Grid container spacing={1.5} mb={2}>
                    <ProfileField label="First Name"     value={p.firstName} />
                    <ProfileField label="Last Name"      value={p.lastName} />
                    <ProfileField label="Gender"         value={p.gender} />
                    <ProfileField label="Date of Birth"  value={p.dob} />
                    <ProfileField label="Birth Time"     value={p.birthTime} />
                    <ProfileField label="Height"         value={p.heightCm ? `${p.heightCm} cm` : null} />
                    <ProfileField label="Marital Status" value={p.maritalStatus} />
                    <ProfileField label="Mother Tongue"  value={p.motherTongue} />
                    <ProfileField label="Religion"       value={p.religion} />
                    <ProfileField label="Community"      value={p.community} />
                    <ProfileField label="City"           value={p.city} />
                    <ProfileField label="State"          value={p.state} />
                  </Grid>

                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Professional</Typography>
                  <Grid container spacing={1.5} mb={2}>
                    <ProfileField label="Education"       value={p.education} />
                    <ProfileField label="Profession"      value={p.profession} />
                    <ProfileField label="Employment Type" value={p.employmentType} />
                    <ProfileField label="Annual Income"   value={p.annualIncome} />
                  </Grid>

                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="subtitle2" fontWeight={700} mb={1.5}>Horoscope</Typography>
                  <Grid container spacing={1.5} mb={2}>
                    <ProfileField label="Star (Nakshatra)" value={p.horoscopeStar} />
                    <ProfileField label="Rasi"             value={p.horoscopeRasi} />
                    <ProfileField label="Gothram"          value={p.horoscopeGothram} />
                  </Grid>

                  {p.aboutMe && (
                    <>
                      <Divider sx={{ mb: 1.5 }} />
                      <Typography variant="subtitle2" fontWeight={700} mb={0.5}>About</Typography>
                      <Typography variant="body2" color="text.secondary">{p.aboutMe}</Typography>
                    </>
                  )}
                </>
              ) : (
                <Alert severity="info">No profile created yet.</Alert>
              )}

              <Divider sx={{ my: 2 }} />
              <Stack direction="row" spacing={1.5} justifyContent="flex-end">
                <Button variant="outlined" startIcon={<EditIcon />} onClick={() => {
                  setProfileDrawer({ open: false, user: null });
                  openEdit(u);
                }} disabled={u.isActive === false}>
                  Edit Settings
                </Button>
                <Button variant="outlined" color="secondary" startIcon={<LockResetIcon />} onClick={() => {
                  setProfileDrawer({ open: false, user: null });
                  openResetPassword(u);
                }}>
                  Reset Password
                </Button>
                <Button variant="outlined" startIcon={<ForumIcon />} onClick={() => setMessaging(u.id)}>
                  Message
                </Button>
                {u.isActive === false ? (
                  <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}
                    onClick={() => { setProfileDrawer({ open: false, user: null }); handleReactivate(u); }} disabled={saving}>
                    Reactivate
                  </Button>
                ) : (
                  <Button variant="outlined" color="error" startIcon={<BlockIcon />}
                    onClick={() => { setProfileDrawer({ open: false, user: null }); setSaveError(''); setDeactivateConfirm(u); }} disabled={saving}>
                    Deactivate
                  </Button>
                )}
              </Stack>
            </Box>
          );
        })()}
      </Drawer>

      {deleting && (
        <DeleteUserDialog user={deleting} onClose={() => setDeleting(null)}
          onDeleted={async (r) => {
            setDeleting(null);
            setProfileDrawer({ open: false, user: null });
            setNotice(`${r.email} was deleted.${r.contractsClosed ? ` ${r.contractsClosed} contract(s) closed.` : ''}${r.bookingsCancelled ? ` ${r.bookingsCancelled} booking(s) cancelled.` : ''}${r.leadReopened ? ' The Marketing lead can be set up again.' : ''}`);
            await refetch();
          }} />
      )}
      {messaging && <SendMessageDialog userId={messaging} onClose={() => setMessaging(null)} />}

      {/* -- Deactivate Confirmation Dialog -- */}
      <Dialog open={!!deactivateConfirm} onClose={() => setDeactivateConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <BlockIcon color="error" />
            <span>Deactivate User?</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1 }}>
            <Typography variant="body2">
              <b>{deactivateConfirm?.email}</b> will be blocked from logging in immediately. You can reactivate them at any time.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" startIcon={<BlockIcon />} onClick={handleDeactivate} disabled={saving}>
            {saving ? 'Deactivating...' : 'Deactivate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -- Reset Password Dialog -- */}
      <Dialog open={resetPwDialog.open} onClose={() => setResetPwDialog({ open: false, user: null, password: '', showPassword: false })} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <LockResetIcon color="secondary" />
            <span>Reset Password</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Alert severity="info" sx={{ mb: 2 }}>
            Setting a new password for <b>{resetPwDialog.user?.email}</b>. They will need to use it on their next login.
          </Alert>
          <TextField
            label="New Password"
            type={resetPwDialog.showPassword ? 'text' : 'password'}
            value={resetPwDialog.password}
            onChange={e => setResetPwDialog(d => ({ ...d, password: e.target.value }))}
            fullWidth
            required
            helperText="Minimum 8 characters"
            InputProps={{
              endAdornment: (
                <IconButton size="small" onClick={() => setResetPwDialog(d => ({ ...d, showPassword: !d.showPassword }))}>
                  {resetPwDialog.showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                </IconButton>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetPwDialog({ open: false, user: null, password: '', showPassword: false })}>Cancel</Button>
          <Button variant="contained" color="secondary" startIcon={<LockResetIcon />} onClick={handleResetPassword} disabled={saving}>
            {saving ? 'Resetting...' : 'Reset Password'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -- Edit User Dialog (Role + Menu Visibility) -- */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, userId: null, roleName: '', menuVisibility: 'selected' })} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <EditIcon color="primary" />
            <span>Edit User Settings</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Stack spacing={3}>
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={editDialog.roleName} label="Role" onChange={e => setEditDialog(d => ({ ...d, roleName: e.target.value }))}>
                {(rolesData?.roles || []).map(r => <MenuItem key={r.id} value={r.name}>{r.displayName}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
              Changing the role also moves the user to that role's <strong>{roleDefaultPlan(editDialog.roleName)}</strong> subscription.
            </Typography>

            <Divider><Typography variant="caption" color="text.secondary" fontWeight={600}>MENU VISIBILITY</Typography></Divider>

            <FormControl fullWidth>
              <InputLabel>Menu Visibility</InputLabel>
              <Select value={editDialog.menuVisibility} label="Menu Visibility" onChange={e => setEditDialog(d => ({ ...d, menuVisibility: e.target.value }))}>
                {MENU_VISIBILITY_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{o.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{o.description}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {editDialog.isFamily && (
              <>
                <Divider><Typography variant="caption" color="text.secondary" fontWeight={600}>ACCOUNT MANAGER</Typography></Divider>
                <Typography variant="caption" color="text.secondary" sx={{ mt: -2 }}>
                  The matchmaker who is this family's — and their vendors' — point of contact until the function. It is set
                  automatically when a matchmaker creates the family's Service Wizard wishlist.
                </Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Account manager</InputLabel>
                    <Select value={editDialog.managerId} label="Account manager" onChange={e => setEditDialog(d => ({ ...d, managerId: e.target.value }))}>
                      <MenuItem value=""><em>None</em></MenuItem>
                      {allUsers.filter(x => x.role?.name === 'matchmaker' && x.isActive !== false).map(m => (
                        <MenuItem key={m.id} value={m.id}>
                          {m.profile ? `${m.profile.firstName} ${m.profile.lastName}` : m.email}{m.phone ? ` · ${m.phone}` : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    label="Until (function date)" type="date" value={editDialog.endsOn} disabled={!editDialog.managerId}
                    onChange={e => setEditDialog(d => ({ ...d, endsOn: e.target.value }))}
                    InputLabelProps={{ shrink: true }} sx={{ minWidth: 200 }}
                  />
                </Stack>
              </>
            )}

            {editDialog.isMatchmaker && (
              <>
                <Divider><Typography variant="caption" color="text.secondary" fontWeight={600}>CONTACT</Typography></Divider>
                <TextField
                  label="Contact number" value={editDialog.phone} fullWidth
                  onChange={e => setEditDialog(d => ({ ...d, phone: e.target.value }))}
                  helperText="Shown to the families this matchmaker manages and to those families' vendors"
                />
              </>
            )}

            {editDialog.menuVisibility === 'selected' && (
              <Alert severity="info">
                <Typography variant="body2" fontWeight={600} mb={0.5}>Visible menus (always):</Typography>
                <Typography variant="body2">My Profile &bull; Matchmaking &bull; Messages &bull; Meetings &bull; Contracts &bull; Wish List</Typography>
                <Typography variant="body2" mt={0.5} color="text.secondary">
                  Additional menus are unlocked automatically based on contracted services.
                </Typography>
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, userId: null, roleName: '', menuVisibility: 'selected' })}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={saving || !editDialog.roleName}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* -- Create User Dialog -- */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonAddIcon color="primary" />
            <span>Create New User</span>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
          <Stack spacing={2.5}>
            <TextField label="Email Address" type="email" value={newUser.email}
              onChange={e => setNewUser(u => ({ ...u, email: e.target.value }))} fullWidth required />
            <TextField label="Password" type="password" value={newUser.password}
              onChange={e => setNewUser(u => ({ ...u, password: e.target.value }))} fullWidth required
              helperText="Minimum 8 characters recommended" />
            <Divider />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select value={newUser.roleName} label="Role" onChange={e => setNewUser(u => ({ ...u, roleName: e.target.value }))}>
                {(rolesData?.roles || []).map(r => <MenuItem key={r.id} value={r.name}>{r.displayName}</MenuItem>)}
              </Select>
            </FormControl>
            <Typography variant="caption" color="text.secondary" sx={{ mt: -1.5 }}>
              Gets the <strong>{roleDefaultPlan(newUser.roleName)}</strong> subscription, so the same permissions as other {roleLabel(newUser.roleName)} users.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" startIcon={<PersonAddIcon />} onClick={handleCreateUser} disabled={saving}>
            {saving ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
